import React, { useEffect, useMemo, useState } from "react";
import { Button, ErrorAlert, Field } from "src/components";
import {
  ALFRED_FIAT_ACCOUNT_TYPE_BY_COUNTRY,
  ALFRED_FIAT_CURRENCY_TO_COUNTRY,
  AlfredFiatAccountFields,
  AlfredFiatAccountType,
  CreateFiatAccountPayload,
} from "src/api";
import { RAIL_RULES, validateFiatAccountFields } from "./alfred-fiat-account-rules";
import "./alfred-add-bank-form.scss";

interface Props {
  sourceCurrency: string | null;
  submitting: boolean;
  error: string | null;
  onSubmit: (payload: Omit<CreateFiatAccountPayload, "session_id">) => void;
  onCancel?: () => void;
}

const AlfredAddBankAccountForm: React.FC<Props> = ({ sourceCurrency, submitting, error, onSubmit, onCancel }) => {
  // Country/rail are derived from the parent dropdown's selected fiat —
  // Alfred enforces `customer.country == operation.country` (errorCode
  // 111417) so a given fiat has exactly one valid rail. There's no
  // type-picker UI; the title surfaces the resolved rail and we render
  // only the rail-specific inputs.
  const inferredType = useMemo<AlfredFiatAccountType | null>(() => {
    if (!sourceCurrency) return null;
    const country = ALFRED_FIAT_CURRENCY_TO_COUNTRY[sourceCurrency.toUpperCase()];
    return country ? ALFRED_FIAT_ACCOUNT_TYPE_BY_COUNTRY[country] ?? null : null;
  }, [sourceCurrency]);
  const inferredCountry =
    sourceCurrency != null ? ALFRED_FIAT_CURRENCY_TO_COUNTRY[sourceCurrency.toUpperCase()] ?? null : null;

  const [fields, setFields] = useState<AlfredFiatAccountFields>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  // Wipe rail-specific fields when the source currency changes (e.g.
  // user switches BRL → MXN before submitting). Schema differs across
  // rails; mixing values would confuse Alfred's validator.
  useEffect(() => {
    setFields({});
    setTouched({});
  }, [inferredType]);

  const ruleDefs = inferredType ? RAIL_RULES[inferredType] : [];
  const fieldErrors = inferredType
    ? validateFiatAccountFields(inferredType, fields as Record<string, unknown>)
    : {};
  const canSubmit = !!inferredType && Object.keys(fieldErrors).length === 0 && !submitting;

  const setField = (key: string, value: string) => setFields((prev) => ({ ...prev, [key]: value }));
  const setBool = (key: string, value: boolean) => setFields((prev) => ({ ...prev, [key]: value }));
  const markTouched = (key: string) => setTouched((prev) => ({ ...prev, [key]: true }));

  // NB: this component renders INSIDE the parent withdrawal `<form>`,
  // and nested forms are invalid HTML — the browser strips the inner
  // form and re-routes its submit button to the outer onSubmit, which
  // is `handleInitiateWithdrawal`. That used to fire the actual
  // withdrawal on every "Save bank" click, debit the user's balance,
  // and bounce them to the page-level loader. We use `<div>` +
  // explicit click handler to keep the parent form out of the picture.
  const handleSave = () => {
    if (!inferredType || !canSubmit) {
      // Mark every rail field as touched so all errors become visible
      // when the user clicks Save on an incomplete form.
      const next: Record<string, boolean> = {};
      ruleDefs.forEach((r) => (next[r.key] = true));
      setTouched(next);
      return;
    }
    onSubmit({ type: inferredType, fiatAccountFields: fields });
  };

  return (
    <div className="alfred-add-bank-form" role="group" aria-label="Add bank account">
      <h3 className="alfred-add-bank-form__title">
        {inferredType
          ? `Add ${inferredType}${inferredCountry ? ` (${inferredCountry})` : ""} account`
          : "Add bank account"}
      </h3>

      {ruleDefs.map((def) => {
        const err = touched[def.key] ? fieldErrors[def.key] : undefined;
        if (def.inputType === "checkbox") {
          const checked = fields[def.key] === true;
          return (
            <label key={def.key} className="alfred-add-bank-form__checkbox">
              <input
                type="checkbox"
                checked={checked}
                onChange={(e) => {
                  setBool(def.key, e.target.checked);
                  markTouched(def.key);
                }}
                disabled={submitting}
              />
              <span>{def.label + (def.required ? " *" : "")}</span>
            </label>
          );
        }
        return (
          <Field
            key={def.key}
            label={def.label + (def.required ? " *" : "")}
            value={((fields as Record<string, unknown>)[def.key] as string) ?? ""}
            onChange={(v) => setField(def.key, v)}
            onBlur={() => markTouched(def.key)}
            placeholder={def.placeholder}
            disabled={submitting}
            error={err}
          />
        );
      })}

      <ErrorAlert message={error} />

      <div className="alfred-add-bank-form__actions">
        {onCancel && (
          <button
            type="button"
            className="alfred-add-bank-form__cancel"
            onClick={onCancel}
            disabled={submitting}
          >
            Cancel
          </button>
        )}
        <Button
          title="Save bank"
          type="button"
          onClick={handleSave}
          disabled={!canSubmit}
          pending={submitting}
          className="deposit-button"
        />
      </div>
    </div>
  );
};

export default AlfredAddBankAccountForm;
